import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import withPortalContainer from '../../../hooks/withPortalContainer';
import ScreenHeader from '../../../components/ScreenHeader';
import { Button } from '@tcl-boron-prefabs/button';
import { Typography } from '@mui/material';
import {
  NeuroFinancialAssistanceContent,
  getNeuroNumberFamilyMembersDropdownOptions,
  getNeuroExtenuatingCircumstancesDropdownOptions,
  NeuroRelationshipToPatient,
  NeuroFinancialAssistanceV2PatientConsent,
  NeuroFinancialAssistanceV2PatientRepresentativeConsent,
  NeuroFinancialAssistanceV2TempusRepresentativeConsent,
  NeuroFinancialAssistanceV2Disclaimer,
  NEURO_FINANCIAL_ASSISTANCE_V2_FORM_KEY,
  getNeuroRelationshipToPatientDropdownOptions,
} from '@tempus/patient-forms-service-shared';
import { Form } from 'react-final-form';
import FormTextInput from '../../../shared/form/FormTextInput';
import FormSingleSelectDropdown from '../../../shared/form/FormSingleSelectDropdown';
import usePortalContext from '../../../context/usePortalContext';
import { required } from '../../../shared/form/validators';
import logger from '../../../../../utils/logger';
import { trackEvent } from '../../../../api/Pendo';
import { BillingInfo } from '../../payment-details/types';
import LoadingIndicator from '../../../components/LoadingIndicator';
import { NotificationType, useNotificationContext } from '../../../notifications/NotificationContext';
import { createUseStyles } from 'react-jss';
import FormCheckboxGroup from '../../../shared/form/FormCheckboxGroup';
import UploadZone, { CachedFile } from '../../../components/UploadZone';
import getBase64File, { buildBase64DataUrl } from '../../../utils/getBase64File';
import FormSingleDatePicker from '../../../shared/form/FormSingleDatePicker';
import FormSignature from '../../../shared/form/FormSignature';
import Condition from '../../../shared/form/ConditionalField';
import { PatientInfo } from '../../patient-info/shared';
import { FinancialAssistanceStatuses } from 'requisition-service-shared';
import { FA_MESSAGE_CONFIG } from '../../../../screens/forms/neuro-financial-assistance/config';
import { useHistory } from 'react-router-dom';
import { SubmissionError } from '../../../components/SubmissionError';

require('cleave.js/dist/addons/cleave-phone.us');

const useStyles = createUseStyles({
  button: {
    maxWidth: '150px',
    flexGrow: 1,
  },
});

export const FinancialAssistanceV2: React.FC<{
  nextAction?: () => Promise<void>;
  skipAction?: () => Promise<void>;
}> = ({ nextAction, skipAction }) => {
  const { fetchHelper, patient } = usePortalContext();
  const classes = useStyles();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [patientInfo, setPatientInfo] = useState<PatientInfo | undefined>();
  const [cachedFile, setCachedFile] = useState<CachedFile | undefined>();
  const [faStatus, setFaStatus] = useState<FinancialAssistanceStatuses | undefined>(undefined);
  const [submissionError, setSubmissionError] = useState(false);
  const { notify } = useNotificationContext();
  const history = useHistory();

  const getPatientInfo = async () => {
    try {
      const data = await fetchHelper<PatientInfo>(`/patient-info`);
      setPatientInfo(data);
    } catch (e: any) {
      logger.error(e, 'Error fetching patient info');
      notify(NotificationType.ERROR, 'Error fetching patient info');
    }
  };

  const fetchFAStatus = async () => {
    try {
      const response = await fetchHelper<BillingInfo>(`/billing-info/${patient.orderId}`);
      if (response.financialAssistanceStatus) {
        setFaStatus(response.financialAssistanceStatus);
        setSubmitted(true);
      }
    } catch (e: any) {
      logger.error(e, 'Error fetching billing info');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    await getPatientInfo();
    await fetchFAStatus();
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onBack = async () => {
    if (skipAction) {
      await skipAction();
    } else {
      history.push('/onboarding/consent-to-test');
    }
  };

  const submitForm = async (data: Record<string, any>) => {
    setSubmitting(true);

    try {
      const submissionData: Record<string, any> = {
        patientFirstName: data.name.first,
        patientLastName: data.name.last,
        patientDateOfBirth: data.dateOfBirth,
        grossAnnualHouseholdIncome: data.grossAnnualHouseholdIncome.replace(/\$/g, '').replace(/,/g, ''),
        numberOfFamilyMembersInHousehold: data.numberOfFamilyMembersInHousehold,
        extenuatingCircumstances: data.extenuatingCircumstances || [],
        relationshipToPatient: data.relationshipToPatient,
        tempusRepresentativeFirstName: data.tempusRepresentativeFirstName,
        tempusRepresentativeLastName: data.tempusRepresentativeLastName,
        representativeFirstName: data.representativeFirstName,
        representativeLastName: data.representativeLastName,
        representativeRelationshipToPatient: data.representativeRelationshipToPatient,
        representativeEmail: data.representativeEmail,
        representativePhoneNumber: data.representativePhoneNumber,
        signature: data.signature,
        acceptableProofOfHouseholdIncome: buildBase64DataUrl(cachedFile?.type ?? '', cachedFile?.content ?? ''),
      };

      await fetchHelper(`/form/submit/${patient.orderId}`, {
        method: 'POST',
        body: {
          formType: NEURO_FINANCIAL_ASSISTANCE_V2_FORM_KEY,
          patientId: patient.patientId,
          orderId: patient.orderId,
          data: submissionData,
        },
      });

      const response = await fetchHelper<BillingInfo>(`/billing-info/${patient.orderId}`);
      if (response.financialAssistanceStatus) {
        trackEvent('FinancialAssistanceSubmitted', {
          orderId: patient.orderId,
          faStatus: response.financialAssistanceStatus,
        });
        setSubmitted(true);
        setFaStatus(response.financialAssistanceStatus);
      } else {
        throw new Error('Financial assistance status not found in response');
      }
    } catch (e: any) {
      logger.error(e, 'Error submitting financial assistance form');
      trackEvent('FinancialAssistanceSubmissionFailed', {
        orderId: patient?.orderId,
        error: e?.message || 'Unknown error',
      });
      setSubmissionError(true);
    }

    setSubmitting(false);
  };

  const handleRetry = () => {
    setSubmissionError(false);
    setCachedFile(undefined);
    fetchData();
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  if (submissionError) {
    return <SubmissionError onRetry={handleRetry} />;
  }

  if (submitted) {
    const messageConfig = FA_MESSAGE_CONFIG[faStatus as string];
    if (!messageConfig) {
      return null;
    }

    return (
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        flexGrow={1}
        gap="16px"
        data-testid="financial-assistance-submitted-card"
      >
        <Grid item container direction="row" alignItems="center" justifyContent="center" gap="16px">
          {messageConfig.icon}
          <Typography variant="h2">{messageConfig.title}</Typography>
        </Grid>
        <Typography variant="body1" textAlign="center">
          {messageConfig.message}
        </Typography>
        <Grid item>
          <Button
            ariaLabel={'Back'}
            onClick={() => (nextAction ? nextAction() : history.push('/onboarding/consent-to-test'))}
            data-testid="back-button"
            classes={{
              root: classes.button,
            }}
          >
            <Typography variant="button">Back</Typography>
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container direction="column" flexGrow={1} gap="16px">
      <ScreenHeader text={'Financial Assistance'} />
      <NeuroFinancialAssistanceContent />
      <Form
        onSubmit={submitForm}
        initialValues={patientInfo}
        render={({ handleSubmit, valid, values }) => (
          <Grid item container direction="column" flexGrow={1} maxHeight="100%">
            <Grid
              item
              container
              direction="column"
              gap="20px"
              data-testid="financial-assistance-form"
              paddingBottom="10px"
              style={{ overflowY: 'auto', flexGrow: 1 }}
            >
              <FormTextInput
                label="Patient First Name*"
                placeholder="Patient first name"
                name="name.first"
                data-testid="patient-first-name"
                disabled
              />
              <FormTextInput
                label="Patient Last Name*"
                placeholder="Patient last name"
                name="name.last"
                data-testid="patient-last-name"
                disabled
              />
              <FormSingleDatePicker label="Patient DOB*" name="dateOfBirth" data-testid="patient-dob" disabled />
              <FormTextInput
                name="grossAnnualHouseholdIncome"
                label="Annual Household Income*"
                validate={required}
                mask={{ numericOnly: true, prefix: '$', numeralThousandsGroupStyle: 'thousand', numeral: true }}
                data-testid="annual-household-income"
              />
              <FormSingleSelectDropdown
                name="numberOfFamilyMembersInHousehold"
                label="Number of Persons in Household*"
                validate={required}
                options={getNeuroNumberFamilyMembersDropdownOptions()}
                data-testid="number-of-family-members"
              />
              <FormCheckboxGroup
                name="extenuatingCircumstances"
                label="Extenuating Circumstances (Select all that apply)"
                options={getNeuroExtenuatingCircumstancesDropdownOptions()}
              />
              <FormSingleSelectDropdown
                label="Relationship to Patient*"
                name="relationshipToPatient"
                validate={required}
                options={getNeuroRelationshipToPatientDropdownOptions()}
                data-testid="relationship-to-patient"
              />
              <Condition when="relationshipToPatient" is={NeuroRelationshipToPatient.TEMPUS_REPRESENTATIVE}>
                <FormTextInput
                  label="Tempus Representative First Name*"
                  placeholder="Representative first name"
                  name="tempusRepresentativeFirstName"
                  validate={required}
                  data-testid="tempus-representative-first-name"
                />
                <FormTextInput
                  label="Tempus Representative Last Name*"
                  placeholder="Representative last name"
                  name="tempusRepresentativeLastName"
                  validate={required}
                  data-testid="tempus-representative-last-name"
                />
              </Condition>
              <Condition
                when="relationshipToPatient"
                is={[
                  NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE,
                  NeuroRelationshipToPatient.TEMPUS_REPRESENTATIVE,
                ]}
              >
                <FormTextInput
                  label={`Representative First Name${
                    values.relationshipToPatient === NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE ? '*' : ''
                  }`}
                  placeholder="Representative first name"
                  name="representativeFirstName"
                  data-testid="representative-first-name"
                  validate={
                    values.relationshipToPatient === NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE
                      ? required
                      : undefined
                  }
                />
                <FormTextInput
                  label={`Representative Last Name${
                    values.relationshipToPatient === NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE ? '*' : ''
                  }`}
                  placeholder="Representative last name"
                  name="representativeLastName"
                  data-testid="representative-last-name"
                  validate={
                    values.relationshipToPatient === NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE
                      ? required
                      : undefined
                  }
                />
                <FormTextInput
                  label="Relationship to Patient"
                  placeholder="Relationship to patient"
                  name="representativeRelationshipToPatient"
                  data-testid="representative-relationship-to-patient"
                />
                <FormTextInput
                  label="Representative Email"
                  name="representativeEmail"
                  placeholder="Representative email"
                  data-testid="representative-email"
                />
                <FormTextInput
                  label="Representative Phone Number"
                  name="representativePhoneNumber"
                  placeholder="Representative phone number"
                  data-testid="representative-phone-number"
                />
              </Condition>
              {values.relationshipToPatient === NeuroRelationshipToPatient.PATIENT && (
                <NeuroFinancialAssistanceV2PatientConsent />
              )}
              {values.relationshipToPatient === NeuroRelationshipToPatient.PATIENT_REPRESENTATIVE && (
                <NeuroFinancialAssistanceV2PatientRepresentativeConsent />
              )}
              {values.relationshipToPatient === NeuroRelationshipToPatient.TEMPUS_REPRESENTATIVE && (
                <NeuroFinancialAssistanceV2TempusRepresentativeConsent />
              )}
              <FormSignature label="Signature*" name="signature" validate={required} data-testid="signature" />
              <NeuroFinancialAssistanceV2Disclaimer />
              <Typography variant="h3">
                Acceptable proof of household income
                <Typography variant="body2" color="textSecondary">
                  Upload your most recent Form 1040, W-2, or pay stub to verify your income.
                </Typography>
              </Typography>
              <UploadZone
                docPreview={cachedFile?.content}
                docType={cachedFile?.type}
                setFile={async (file) => {
                  if (file) {
                    const base64File = (await getBase64File(file)) as string;
                    setCachedFile({
                      name: file.name,
                      type: file.type,
                      content: base64File,
                    } as CachedFile);
                  } else {
                    setCachedFile(undefined);
                  }
                }}
                width="100%"
                data-testid="financial-assistance-upload-zone"
              />
              {cachedFile?.content ? (
                <Button
                  buttonType="tertiary"
                  small
                  onClick={() => setCachedFile(undefined)}
                  ariaLabel="Remove"
                  data-testid="remove-button"
                >
                  Remove
                </Button>
              ) : null}
            </Grid>
            <Grid
              item
              container
              flexGrow={1}
              padding="30px 0px"
              gap="15px"
              justifyContent="center"
              alignItems="flex-end"
              position="sticky"
              bottom={0}
              bgcolor="#ffffff"
            >
              <Button
                ariaLabel={'Back'}
                onClick={onBack}
                buttonType="tertiary"
                disabled={submitting}
                data-testid="back-button"
                classes={{
                  root: classes.button,
                }}
              >
                <Typography variant="button">Back</Typography>
              </Button>
              <Button
                ariaLabel={'Continue'}
                onClick={handleSubmit}
                loading={submitting}
                disabled={!valid || !cachedFile?.content || submitting}
                data-testid="continue-button"
                data-pendo="financial-assistance-submit-button"
                classes={{
                  root: classes.button,
                }}
              >
                <Typography variant="button">Submit</Typography>
              </Button>
            </Grid>
          </Grid>
        )}
      />
    </Grid>
  );
};

export default withPortalContainer(FinancialAssistanceV2);

export const OnboardingFinancialAssistance: React.FC<{ next?: () => Promise<void>; skip?: () => Promise<void> }> = ({
  next,
  skip,
}) => <FinancialAssistanceV2 nextAction={next} skipAction={skip} />;
